import { Paginated, Pagination } from "@/lib/interfaces/utils";
import { Link2Off, Plus, X } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./button";
import ConfirmationDialog from "./confirmation-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { SearchableDropdown } from "./searchable-dropdown";

interface LinkedItemManagerProps<T> {
  fetchSearchResults: (pagination: Pagination, searchText: string) => Promise<Paginated<T>>;
  renderSearchOption: (item: T) => { label: string; value: string };
  onCreateNew?: (inputText: string) => void;
  customOnSubmit: (id: string, title: string) => Promise<void>;
  link?: {
    title: string;
    href: string;
    id: string;
  };
  removeItem?: (id: string) => Promise<void>;
  multiple?: boolean;
  title: string;
  editor?: React.ReactElement;
}

// Update the component definition to use the generic type
function LinkedItemManager<T>({
  fetchSearchResults,
  renderSearchOption,
  onCreateNew = () => {},
  customOnSubmit,
  link,
  removeItem,
  multiple,
  title,
  editor,
}: LinkedItemManagerProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [initialText, setInitialText] = useState("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const handleSubmit = async (id: string, title: string) => {
    await customOnSubmit(id, title);
    setIsOpen(false);
    setIsEditorOpen(false);
    setShowSearch(false);
  };

  const handleCreateNew = (inputText: string) => {
    setIsEditorOpen(true);
    setInitialText(inputText);
    setShowSearch(false);
    onCreateNew?.(inputText);
  };

  return (
    <div className="flex gap-2 flex-col">
      {showSearch ? (
        <>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            {(multiple || !link) && (
              <SearchableDropdown
                fetchData={fetchSearchResults}
                renderOption={renderSearchOption}
                onSelect={handleSubmit}
                onCreateNew={handleCreateNew}
                placeholder={`Durchsuche ${title}${title === "Pipeline" ? "s" : title === "Deals" ? "" : "en..."}...`}
                createNewLabel={editor ? `Neue ${title} erstellen` : ""}
              />
            )}
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Link {title}</DialogTitle>
              </DialogHeader>
              <SearchableDropdown
                fetchData={fetchSearchResults}
                renderOption={renderSearchOption}
                onSelect={handleSubmit}
                onCreateNew={handleCreateNew}
                placeholder={`Durchsuche ${title}${title === "Pipeline" ? "s" : title === "Deals" ? "" : "en..."}...`}
                createNewLabel={editor ? `Neue ${title} erstellen` : ""}
              />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="flex justify-between">
          {link ? (
            <div className="flex items-center gap-2">
              <Link to={link.href} className="text-blue-600 hover:text-blue-800 hover:underline">
                {link.title}
              </Link>
            </div>
          ) : (
            <div className="text-gray-500 italic flex items-center gap-2">
              {multiple ? "Weitere" : "Keine"} {title} {multiple ? "verknüpfen" : "verknüpft"}
              {!multiple && <Link2Off className="h-4 w-4" />}
            </div>
          )}
          {removeItem && link ? (
            <ConfirmationDialog
              onConfirm={() => removeItem(link.id)}
              title="Verbindung trennen"
              description="Sind Sie sicher, dass Sie diese Verbindung trennen möchten?"
              variant="destructive"
            >
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800">
                <X className="h-4 w-4" />
              </Button>
            </ConfirmationDialog>
          ) : (
            <>
              <Button onClick={() => setShowSearch(true)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800">
                <Plus className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}
      {editor && isEditorOpen && (
        <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add {title}</DialogTitle>
            </DialogHeader>
            {React.cloneElement(editor, {
              initialText,
              onSubmitDone: (id: string, title: string) => {
                setIsEditorOpen(false);
                handleSubmit(id, title);
              },
            })}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default LinkedItemManager;
